import { action, computed, makeAutoObservable, observable } from "mobx";
import { SystemItemTypes } from "../enums";
import {
  AuthService,
  IAPIPaginationResponse,
  PaginationModel,
} from "@mindmemobile/mm-shared-global";
import {
  PurchaseLinkDetailPageModel,
  PurchaseLinkGridModel,
  PurchaseLinkModel,
} from "../models/PurchaseLinkModels";
import { MerchantAccount } from "../models/MerchantApplicationInputModel";
import { SellerModel, withSellerPolicyCopy } from "../models/SellerModels";
import { IAPIPurchaseLinkGridModel } from "../interfaces/IAPIPurchaseLinkModel";
import { ItemDetailPageModel } from "../models/ItemDetailPageModels";
import { PricingPageInputModel } from "../models/PriceInputs.model";
import { RelatedSectionModel } from "../models/DonationAndItemModel";
import { ProductAttributeInputModel } from "../models/ProductModel";
import SellerService from "../services/SellerService";
import { IAPICreateSellerModel } from "../interfaces/SellerInterfaces";

export default class PurchaseLinkStore {
  @observable purchaseLinkModel: PurchaseLinkModel;
  @observable merchantAccounts: MerchantAccount[] = [];
  @observable detailPage: PurchaseLinkDetailPageModel =
    new PurchaseLinkDetailPageModel();
  public pagination: PaginationModel = new PaginationModel({
    pageNumber: 1,
    pageSize: 999999,
  });
  @observable gridData: PurchaseLinkGridModel[] = [];
  @observable sellers: SellerModel[] = [];
  /* Sellers whose terms / return policy copy has been fetched from Seller/Get.
     GetSellerNames, which fills `sellers`, returns the seller's identity only,
     so a seller resolved from that list always looks like it has no policies. */
  @observable policyResolvedSellerIds: string[] = [];
  /* The seller this link was loaded with. Its saved policy choices were made deliberately, so they
     survive even if the seller's copy has since been emptied in Payment Settings - resetting them
     on load would hide the selection and, on the next save, quietly rewrite it. Picking a different
     seller is another matter: those choices were never made for that seller. */
  @observable sellerTermsBaselineSellerId: string | null = null;
  /* Whether the link was LOADED with each policy on display. Drives whether the builder offers the
     dropdown for a seller that has no copy. Deliberately a snapshot, not a read of the live value:
     keying off the current value would make the dropdown delete itself the moment the user picked
     "Do Not Display" in it, with no way back. */
  @observable sellerTermsWasConfigured: boolean = false;
  @observable sellerReturnPolicyWasConfigured: boolean = false;

  constructor() {
    this.purchaseLinkModel = new PurchaseLinkModel();
    makeAutoObservable(this);
  }

  /**
   * Selected merchant account with its payment settings resolved.
   *
   * The purchase link edit API returns the account without paymentSettings
   * (MerchantAccount.deserializeAPIModel2), so match it against the fetched
   * list, which carries the full settings.
   */
  @computed
  get selectedMerchantAccountWithSettings(): MerchantAccount {
    const selected = this.purchaseLinkModel.selectedMerchantAccount;
    return (
      this.merchantAccounts.find(
        (account) =>
          (!!selected.merchantAccountId &&
            account.merchantAccountId === selected.merchantAccountId) ||
          (!!selected.id && account.id === selected.id),
      ) || selected
    );
  }

  @action
  updateSellers(sellers: SellerModel[]) {
    /* The reloaded list carries no policy copy, so replacing outright drops
       whatever was merged into the previous entries and hides the Terms
       dropdowns until the per-seller fetch lands again. Carry the known copy
       over, and still clear the resolved ids so edits made since are picked
       up. */
    const previous = this.sellers;
    this.sellers = sellers.map((seller) =>
      withSellerPolicyCopy(
        seller,
        previous.find((known) => known.id === seller.id),
      ),
    );
    this.policyResolvedSellerIds = [];
  }

  /**
   * The selected seller as GetSellerNames currently knows it, carrying the
   * policy copy from wherever it is available - the names list returns the
   * seller's identity only. Falls back to the link's own seller while the
   * list is still loading, or when the seller is no longer in it.
   */
  @computed
  get selectedSellerDetails(): SellerModel | undefined {
    const selected = this.purchaseLinkModel?.selectedSeller;
    if (!selected?.id) return selected;
    const fromList = this.sellers.find((seller) => seller.id === selected.id);
    if (!fromList) return selected;
    /* Identity comes from the list, the policy copy from whichever of the two
       has it. GetSellerNames returns no copy at all, so taking the list entry
       outright discards the copy the link was loaded with, which hides the
       Terms dropdowns for a seller whose terms and return policy do exist. */
    return withSellerPolicyCopy(fromList, selected);
  }

  /**
   * Terms and the return policy can only be presented at checkout when the
   * selected seller actually has that copy, so anything the seller is missing
   * is forced back to "Do Not Display" before it can be saved on the link.
   */
  /**
   * True once the selected seller's policy copy is known: either it is already
   * on the seller, or Seller/Get has answered for it. Until then the Terms
   * section has nothing to go on, so it neither shows the dropdowns nor
   * rewrites what the link saved.
   */
  @computed
  get isSelectedSellerPolicyResolved(): boolean {
    const seller = this.selectedSellerDetails;
    if (!seller?.id) return true;
    return (
      seller.hasTermsAndConditions ||
      seller.hasReturnPolicy ||
      this.policyResolvedSellerIds.includes(seller.id)
    );
  }

  /**
   * Pulls the selected seller's terms and return policy from Seller/Get.
   *
   * The builders resolve the seller from GetSellerNames, which returns the
   * seller's identity only - no policy copy - so both flags read false there
   * and the Terms dropdowns stay hidden however the seller is set up.
   * Seller/Get is the endpoint that carries the copy (it is what Payment
   * Settings edits), so fetch it once per seller and merge the copy in.
   */
  @action
  loadSelectedSellerPolicies() {
    const sellerId = this.purchaseLinkModel?.selectedSeller?.id;
    if (!sellerId || this.policyResolvedSellerIds.includes(sellerId)) return;
    SellerService.getSeller(AuthService.authData.accountId, sellerId).subscribe(
      {
        next: (response) => {
          if (response.result) {
            this.mergeSellerPolicies(sellerId, response.result);
          }
        },
        /* Left unresolved on failure so the next open retries, rather than the
           seller being treated as having no policies. */
        error: () => undefined,
      },
    );
  }

  @action
  mergeSellerPolicies(sellerId: string, data: IAPICreateSellerModel) {
    const policies = {
      termsAndConditions: data.termsAndConditions || "",
      termsAndConditionsEditorRawContent:
        data.termsAndConditionsEditorRawContent || "",
      returnPolicy: data.returnPolicy || "",
      returnPolicyEditorRawContent: data.returnPolicyEditorRawContent || "",
    };
    this.sellers = this.sellers.map((seller) =>
      seller.id === sellerId
        ? new SellerModel({ ...seller, ...policies })
        : seller,
    );
    /* Also on the link's own seller: it is what selectedSellerDetails falls
       back to while the list is still loading. */
    if (this.purchaseLinkModel?.selectedSeller?.id === sellerId) {
      this.updatePurchaseLink(
        new PurchaseLinkModel({
          ...this.purchaseLinkModel,
          selectedSeller: new SellerModel({
            ...this.purchaseLinkModel.selectedSeller,
            ...policies,
          }),
        }),
      );
    }
    if (!this.policyResolvedSellerIds.includes(sellerId)) {
      this.policyResolvedSellerIds = [
        ...this.policyResolvedSellerIds,
        sellerId,
      ];
    }
    this.applySellerTermsAvailability();
  }

  /**
   * Called once when the popup opens on a SAVED link. Records the seller it was saved with, and
   * whether each policy was on display, so those deliberate choices survive even if the seller's
   * copy has since been emptied. Never called when creating, so a new link always follows the
   * seller. Idempotent - the first call wins, later ones must not overwrite with post-reset values.
   */
  /**
   * Clears what captureSellerTermsBaseline recorded. The store is a module-level singleton, so a
   * link opened for editing leaves its baseline and snapshots behind; without this, creating a link
   * straight afterwards would inherit them - keeping the dropdowns on screen for a seller with no
   * copy, showing the "Do Not Display" the reset had just written.
   */
  @action
  clearSellerTermsBaseline() {
    this.sellerTermsBaselineSellerId = null;
    this.sellerTermsWasConfigured = false;
    this.sellerReturnPolicyWasConfigured = false;
  }

  @action
  captureSellerTermsBaseline() {
    if (this.sellerTermsBaselineSellerId !== null) return;
    this.sellerTermsBaselineSellerId =
      this.purchaseLinkModel.selectedSeller?.id || "";
    this.sellerTermsWasConfigured =
      this.purchaseLinkModel.sellerTermsAndConditions !== "DoNotDisplay";
    this.sellerReturnPolicyWasConfigured =
      this.purchaseLinkModel.sellerReturnPolicy !== "DoNotDisplay";
  }

  @action
  applySellerTermsAvailability() {
    const seller = this.selectedSellerDetails;
    if (!seller) return;
    /* Nothing is known about the seller's policies yet, so resetting now would
       wipe a saved selection the seller can in fact present. */
    if (!this.isSelectedSellerPolicyResolved) return;
    /* The baseline is never self-seeded here: on a new link the popup auto-selects the first
       seller, which is indistinguishable from a saved one at this point. Only captureSellerTermsBaseline,
       called by the popup when it opens in edit mode, sets it - so a new link always resets. */
    if (seller.id && seller.id === this.sellerTermsBaselineSellerId) return;
    const { purchaseLinkModel } = this;
    const sellerTermsAndConditions = seller.hasTermsAndConditions
      ? purchaseLinkModel.sellerTermsAndConditions
      : "DoNotDisplay";
    const sellerReturnPolicy = seller.hasReturnPolicy
      ? purchaseLinkModel.sellerReturnPolicy
      : "DoNotDisplay";
    if (
      sellerTermsAndConditions === purchaseLinkModel.sellerTermsAndConditions &&
      sellerReturnPolicy === purchaseLinkModel.sellerReturnPolicy
    ) {
      return;
    }
    this.updatePurchaseLink(
      new PurchaseLinkModel({
        ...purchaseLinkModel,
        sellerTermsAndConditions,
        sellerReturnPolicy,
      }),
    );
  }

  @action
  updatePurchaseLink(data: PurchaseLinkModel) {
    this.purchaseLinkModel = new PurchaseLinkModel(data);
  }

  @action
  updateDetailPage(data: PurchaseLinkDetailPageModel) {
    this.detailPage = new PurchaseLinkDetailPageModel(data);
  }

  deserializeAPIResponse(
    data: IAPIPurchaseLinkGridModel[],
    pagination: IAPIPaginationResponse,
  ): void {
    let isFirst = true;
    this.gridData =
      (data &&
        data.map((purchaseLink: any, index: number) => {
          const result = PurchaseLinkGridModel.deserializeAPIModel(
            purchaseLink,
            isFirst,
          );
          isFirst = false; // Ensure only the first record gets true
          return result;
        })) ||
      [];
    this.pagination = PaginationModel.deserialize(pagination);
  }

  @action
  updateMerchantAccounts(data: MerchantAccount[]) {
    this.merchantAccounts = data;
  }

  @action
  copySelectedItemDetails(
    itemDetails: ItemDetailPageModel,
    pricingInputs: PricingPageInputModel[] | null,
    relatedSectionData: RelatedSectionModel | null,
    itemType: keyof typeof SystemItemTypes,
    variants: ProductAttributeInputModel | null,
    itemId: string,
    isTrackInventory: boolean,
  ) {
    this.updatePurchaseLink(
      new PurchaseLinkModel({
        ...this.purchaseLinkModel,
        itemDetails: new ItemDetailPageModel(itemDetails),
        pricingInputs:
          pricingInputs &&
          pricingInputs.map((price) => new PricingPageInputModel(price)),
        relatedSectionData:
          relatedSectionData && new RelatedSectionModel(relatedSectionData),
        itemType: itemType,
        variants: variants,
        itemId: itemId,
        isTrackInventory: isTrackInventory,
      }),
    );
  }

  @action
  resetPurchaseLink() {
    this.purchaseLinkModel = new PurchaseLinkModel();
  }
}
